import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { RecorderController } from './RecorderController';
import { DocumentPipController, type DocumentPipSnapshot } from './DocumentPipController';
import { RecorderControlHost } from './RecorderFloatingControl';
import {
  RecordingSegmentController,
  type RecordingSegmentSnapshot,
} from './RecordingSegmentController';
import { IndexedDbRecorderStore } from './recorderDb';
import { IndexedDbSegmentSettlementStore } from './segmentSettlementDb';
import { IndexedDbRecordingSnapshotStore } from './snapshotDb';
import {
  RecordingSnapshotController,
  type RecordingSnapshotUploadSnapshot,
} from './RecordingSnapshotController';
import type { RecorderSnapshot } from './types';
import { registerRecorderSignOutHandler } from './recorderSignOut';
import { installRecorderDiagnosticBuffer } from './recorderDiagnostics';
import {
  RecordingUploadController,
  type RecordingUploadSnapshot,
} from './RecordingUploadController';

type RecorderContextValue = {
  controller: RecorderController;
  /** Drop this browser's copy of a walkthrough that is filed or discarded. */
  releaseLocalRecording: (recordingId: string) => Promise<void>;
  snapshot: RecorderSnapshot;
  segmentController: RecordingSegmentController;
  segments: RecordingSegmentSnapshot;
  pipController: DocumentPipController;
  pip: DocumentPipSnapshot;
  uploadController: RecordingUploadController;
  uploads: RecordingUploadSnapshot;
  snapshotController: RecordingSnapshotController;
  snapshotUploads: RecordingSnapshotUploadSnapshot;
};

const RecorderContext = createContext<RecorderContextValue | null>(null);

/** The phases in which a recording is being started, made or stopped. */
const UNDER_WAY: ReadonlySet<RecorderSnapshot['phase']> = new Set([
  'requesting-display',
  'requesting-microphone',
  'starting',
  'recording',
  'stopping',
]);

export function RecorderProvider({ children }: { children: ReactNode }) {
  const { user, features } = useAuth();
  /**
   * Whether the recorder answers this user, not whether they may start one.
   * It was staff and the start flag together, so a person whose workspace was
   * switched off mid-upload had their recovery state cleared and the upload
   * stranded. A walkthrough that started with access may finish (TOADO-493):
   * uploads resume, and the controls stay, while the recorder is open to them.
   */
  const recorderOpen = !!user?.id && features.v2RecorderRoutes;
  const authenticatedOwnerRef = useRef<string | null>(user?.id ?? null);
  const uploadControllerRef = useRef<RecordingUploadController | null>(null);
  const snapshotControllerRef = useRef<RecordingSnapshotController | null>(null);
  authenticatedOwnerRef.current = user?.id ?? null;
  const [store] = useState(() => new IndexedDbRecorderStore());
  const [settlementStore] = useState(() => new IndexedDbSegmentSettlementStore());
  const [snapshotStore] = useState(() => new IndexedDbRecordingSnapshotStore());
  const [controller] = useState(() => new RecorderController({
    store,
    server: {
      create: api.createRecording,
      get: api.getRecording,
      discard: api.discardRecording,
      alive: api.recordingAlive,
    },
    beforeDiscard: async (recordingId) => {
      await uploadControllerRef.current?.prepareDiscard(recordingId);
      await snapshotControllerRef.current?.prepareDiscard(recordingId);
      await settlementStore.deleteSegmentSettlementsForRecording(recordingId);
    },
  }));
  const [snapshotController] = useState(() => new RecordingSnapshotController(controller, {
    store: snapshotStore,
    server: {
      preflight: api.preflightRecordingSnapshot,
      upload: api.uploadRecordingSnapshot,
    },
    getAuthenticatedOwnerId: () => authenticatedOwnerRef.current,
  }));
  snapshotControllerRef.current = snapshotController;
  const [segmentController] = useState(() => new RecordingSegmentController(controller, {
    server: {
      get: api.getRecording,
      open: api.openRecordingSegment,
      close: api.closeRecordingSegment,
      cancel: api.cancelRecordingSegment,
    },
    settlements: settlementStore,
    prepareMoment: (recordingId) => snapshotController.prepareMoment(recordingId),
    releaseMoment: (permit) => snapshotController.releaseMoment(permit),
    captureMoment: (request, permit) => snapshotController.capture(request, permit),
    getAuthenticatedOwnerId: () => authenticatedOwnerRef.current,
  }));
  const [uploadController] = useState(() => new RecordingUploadController(controller, {
    store,
    server: {
      getUpload: api.getRecordingMasterUpload,
      uploadPart: api.uploadRecordingMasterPart,
      getRecording: api.getRecording,
      retryClips: api.retryRecordingClips,
    },
  }));
  uploadControllerRef.current = uploadController;
  const [pipController] = useState(() => new DocumentPipController(window));
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  const segments = useSyncExternalStore(
    segmentController.subscribe,
    segmentController.getSnapshot,
    segmentController.getSnapshot,
  );
  const pip = useSyncExternalStore(
    pipController.subscribe,
    pipController.getSnapshot,
    pipController.getSnapshot,
  );
  const uploads = useSyncExternalStore(
    uploadController.subscribe,
    uploadController.getSnapshot,
    uploadController.getSnapshot,
  );
  const snapshotUploads = useSyncExternalStore(
    snapshotController.subscribe,
    snapshotController.getSnapshot,
    snapshotController.getSnapshot,
  );

  useEffect(() => {
    const disconnectSegments = segmentController.connect();
    const disconnectPip = pipController.connect();
    const disconnectUploads = uploadController.connect();
    return () => {
      disconnectSegments();
      disconnectPip();
      disconnectUploads();
      segmentController.dispose();
      pipController.dispose();
      uploadController.dispose();
      snapshotController.dispose();
    };
  }, [pipController, segmentController, snapshotController, uploadController]);

  useEffect(() => {
    pipController.setActive(recorderOpen && UNDER_WAY.has(snapshot.phase));
  }, [pipController, recorderOpen, snapshot.phase]);

  useEffect(() => {
    if (user?.id && recorderOpen) {
      void controller.loadRecoveries(user.id);
      void segmentController.reconcilePending(user.id);
      void uploadController.resumeOwner(user.id).catch(() => {
        window.dispatchEvent(new CustomEvent('vd:recorder-diagnostic', {
          detail: { code: 'recording_upload_recovery_unavailable', phase: 'upload' },
        }));
      });
      void snapshotController.resumeOwner(user.id).catch(() => {
        window.dispatchEvent(new CustomEvent('vd:recorder-diagnostic', {
          detail: { code: 'snapshot_upload_recovery_unavailable', phase: 'snapshot' },
        }));
      });
      return;
    }
    snapshotController.clearOwner();
    uploadController.clearOwner();
    void segmentController.prepareForSignOut().finally(() => controller.clearUserState());
  }, [controller, recorderOpen, segmentController, snapshotController, uploadController, user?.id]);

  useEffect(() => registerRecorderSignOutHandler({
    prepare: async () => {
      await uploadController.prepareForSignOut();
      await snapshotController.prepareForSignOut();
      await segmentController.prepareForSignOut();
    },
    cancel: () => controller.cancelSignOut(),
  }), [controller, segmentController, snapshotController, uploadController]);

  useEffect(() => {
    if (!user?.id || !recorderOpen) return undefined;
    const retry = () => {
      void segmentController.reconcilePending(user.id);
      uploadController.retryAll();
      snapshotController.retryAll();
    };
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [recorderOpen, segmentController, snapshotController, uploadController, user?.id]);

  useEffect(() => installRecorderDiagnosticBuffer(), []);

  useEffect(() => {
    // Prune expired metadata even for a different account. Rows remain owner
    // isolated and survive sign-out only until their server retention expiry.
    void settlementStore.pruneExpiredSegmentSettlements(new Date()).catch(() => {
      // Cleanup is best effort. IndexedDB may be unavailable in private or
      // hardened browser contexts and must not create an unhandled rejection.
    });
  }, [settlementStore]);

  /**
   * Drop this browser's copy of a walkthrough that is finished with, filed or
   * discarded from its review page, without telling the server anything.
   *
   * Committing or discarding there leaves the local chunks and upload record
   * behind. That record outlives its server session's active window, so the
   * recorder page — whose list asks the server only for sessions that still
   * hold a slot — sees an id the server never mentions and puts it back on
   * screen as an unfinished walkthrough waiting for review. Sweeping here
   * removes the cause rather than teaching the list to recognise ghosts. The
   * recorder also stops offering it as just finished: it was filed or thrown
   * away (review of 0.2.0, 3 Oct).
   */
  const releaseLocalRecording = useCallback(async (recordingId: string) => {
    await uploadController.prepareDiscard(recordingId);
    await snapshotController.prepareDiscard(recordingId);
    await settlementStore.deleteSegmentSettlementsForRecording(recordingId);
    await store.deleteSession(recordingId).catch(() => {});
    if (controller.getSnapshot().session?.id === recordingId) controller.reset();
  }, [controller, settlementStore, snapshotController, store, uploadController]);

  const value = useMemo(() => ({
    controller,
    releaseLocalRecording,
    snapshot,
    segmentController,
    segments,
    pipController,
    pip,
    uploadController,
    uploads,
    snapshotController,
    snapshotUploads,
  }), [controller, pip, pipController, releaseLocalRecording, segmentController, segments, snapshot, snapshotController, snapshotUploads, uploadController, uploads]);
  return (
    <RecorderContext.Provider value={value}>
      {children}
      {recorderOpen && (
        <RecorderControlHost
          pip={pip}
          controller={segmentController}
          snapshot={segments}
          elapsedMs={snapshot.elapsedMs}
          recorderPhase={snapshot.phase}
          snapshotUploads={snapshotUploads}
          onRetrySnapshot={(key) => snapshotController.retrySnapshot(key)}
        />
      )}
    </RecorderContext.Provider>
  );
}

const NO_LOCAL_COPY = async () => {};

/**
 * The local-release capability on its own, tolerating no provider.
 *
 * The review page needs exactly this one thing from the recorder and nothing
 * else, so it should not take a hard dependency on the whole context — which
 * owns three IndexedDB stores and a media pipeline — to get it. Where no
 * recorder is mounted the no-op is the correct answer rather than a fallback:
 * there is no local copy to release.
 */
export function useReleaseLocalRecording(): (recordingId: string) => Promise<void> {
  return useContext(RecorderContext)?.releaseLocalRecording ?? NO_LOCAL_COPY;
}

/** Whether a recording is under way; false where no recorder is mounted, as nothing can be recording there. */
export function useRecordingUnderWay(): boolean {
  const phase = useContext(RecorderContext)?.snapshot.phase;
  return phase !== undefined && UNDER_WAY.has(phase);
}

export function useRecorder(): RecorderContextValue {
  const context = useContext(RecorderContext);
  if (!context) throw new Error('useRecorder must be used inside RecorderProvider');
  return context;
}
